// @ts-strict-ignore
import {ConnectivityState, ExportState} from "@models";
import {Component, ElementRef, HostListener, Input, NgZone, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild} from "@angular/core";
import {SimConfigurationService, SimMaterialDialogService, SimCameraService, SimUtilityService} from "@services";
import {TranslateService} from "@ngx-translate/core";
import {Subject} from "rxjs";
import {Translations} from "@translations";
import {operation} from "retry";

const {pipelines} = window["mediaStreamLibrary"];

type AxisMediaStreamLibraryVideoOptions = {
	cameraId: string;
	ipAddress: string;
	url: string;
	anonymousAccessAllowed: boolean;
	status: ConnectivityState;
};

@Component({
	selector: "sim-axis-media-stream-library-video",
	templateUrl: "./axis-media-stream-library-video.component.html",
	styleUrls: ["./axis-media-stream-library-video.component.scss"],
	standalone: false,
})
export class AxisMediaStreamLibraryVideoComponent implements OnInit, OnChanges, OnDestroy {
	T = Translations;
	ExportState = ExportState;
	settings = SimConfigurationService.settings;

	@ViewChild("axisVideo", {static: true})
	axisVideoElement: ElementRef<HTMLVideoElement>;
	public websocketUri: string;
	public rtspUri: string;

	private get axisVideo(): HTMLVideoElement {
		return this.axisVideoElement?.nativeElement;
	}

	private lastCurrentTime: number = null;
	@Input()
	axisMediaStreamLibraryVideoOptions: AxisMediaStreamLibraryVideoOptions = null;
	@Input() axisMediaStreamLibraryVideoStyle = "";
	@Input() axisMediaStreamLibraryVideoClass = "";
	@Input() axisMediaStreamLibraryVideoIsMuted = true;

	private reconnectInterval = 10_000;

	public updateVideoStreamStatisticThread: any = null;
	public videoStreamStatistic: {
		latency: number;
		playbackRate: number;
		droppedFrames: string;
		droppedVideoSegments: number;
		codec: {coding: string; profile: string; level: string};
	} = null;

	public pipelineCheckerThread: any = null;
	public pipelineCheckerErrorCounter = 0;
	public isPlayerVisible = true;
	public pipeline: any = null;

	private lastTimeUpdate: number = null;

	public avpState: "Ready" | "Offline" | "Loading" | "LoadingFailed" | "Idle" | "Reconnecting" | "ReconnectingFailed" | "Unauthorized" =
		"Idle";

	private streamCheckerThread: any = null;

	private initializePlayerPromise: Promise<any>;

	public showDebugStatistics: boolean = false;
	debugContainerHeight: number = 40;
	@ViewChild("debugContainer") debugContainer: ElementRef;

	public $OnDestroy = new Subject<void>();

	constructor(
		private _simUtilityService: SimUtilityService,
		private _simCameraService: SimCameraService,
		private _simDialogService: SimMaterialDialogService,
		private translate: TranslateService,
		private _zone: NgZone,
	) {}

	public ngOnInit() {
		this.initializePipelineChecker();
		this.initializeStreamChecker();

		this.lastTimeUpdate = Date.now();
	}

	public ngOnChanges(changesObj: SimpleChanges): void {
		const options = changesObj["axisMediaStreamLibraryVideoOptions"];

		const currentOptions = options.currentValue as AxisMediaStreamLibraryVideoOptions;
		const previousOptions = options.previousValue as AxisMediaStreamLibraryVideoOptions;

		if (options) {
			const isNoVideoUrl =
				currentOptions && currentOptions.url && currentOptions.url.indexOf(SimConfigurationService.settings.noVideoUrl) >= 0;

			const isOffline = currentOptions && currentOptions.status === ConnectivityState.Offline;

			const hasChanged =
				(currentOptions &&
					previousOptions &&
					// Check ip changed
					(currentOptions.ipAddress !== previousOptions.ipAddress ||
						// Check state changed
						currentOptions.status !== previousOptions.status ||
						//stream url changed
						currentOptions.url !== previousOptions.url)) ||
				options.isFirstChange();

			if (hasChanged) {
				if (isOffline) {
					this.avpState = "Offline";
				} else if (!isNoVideoUrl && !isOffline) {
					this.initializePlayerSafely();
				}
			}
		}

		if (changesObj["axisMediaStreamLibraryVideoIsMuted"]) {
			if (changesObj["axisMediaStreamLibraryVideoIsMuted"].currentValue) {
				this.mute();
			} else {
				this.unmute();
			}
		}
	}

	public ngOnDestroy(): void {
		//console.warn("destroy axis start");
		this.$OnDestroy.next();
		this.$OnDestroy.complete();

		this.destroyPlayer();
		this.axisVideo?.remove();
		this.closePipelineCheckerThread();
		this.closeStreamCheckerThread();
		this.closeVideoStreamStatisticsThread();
		//console.warn("destroy axis finised");
	}

	@HostListener("document:visibilitychange")
	public $onVisibilityChange(): any {
		if (this.axisMediaStreamLibraryVideoOptions.status === ConnectivityState.Online) {
			if (window.document.visibilityState === "visible") {
				this.avpState = "Loading";
				this.initializePlayerSafely().then(() => {
					this.isPlayerVisible = true;
				});
			} else if (window.document.visibilityState === "hidden") {
				this.avpState = "Idle";
				this.destroyPlayer();
				this.closeVideoStreamStatisticsThread();
				this.isPlayerVisible = false;
			}
		}
	}

	@HostListener("window:beforeunload")
	public $onWindowDestroy() {
		this.ngOnDestroy();
	}

	private initializeStreamChecker() {
		if (this.settings.axisStreamCheckerThreadEnabled) {
			if (this.streamCheckerThread) {
				clearInterval(this.streamCheckerThread);
			}
			this._zone.runOutsideAngular(() => {
				this.streamCheckerThread = setInterval(() => {
					if (this.axisVideo?.buffered?.length > 0) {
						const start = this.axisVideo?.buffered.start(0);
						const end = this.axisVideo?.buffered.end(0);
						const currentTime = this.axisVideo?.currentTime;

						if (currentTime < start) {
							this._zone.run(() => {
								this.axisVideo.currentTime = end - 0.1;
								this.axisVideo?.play().then(
									() => {
										if (this.videoStreamStatistic) {
											if (this.videoStreamStatistic.droppedVideoSegments) {
												this.videoStreamStatistic.droppedVideoSegments++;
											} else {
												this.videoStreamStatistic.droppedVideoSegments = 1;
											}
										}

										console.warn("Video Segment lost ... skipped.");
									},
									(reason: any) => {
										console.error(reason);
									},
								);
							});
						}
					}
				}, this.settings.axisStreamCheckerThreadInterval);
			});
		}
	}

	/**
	 * The checker should solve following problems:
	 * 1) Recover the video stream after random network errors
	 * 2) If you press F5 or click the reload button in your browser the player can get the stream to play right
	 *
	 * Solution:
	 * If the problem happens when you look at the browser window (check visibilityState) and the player should be ready (avpState) and
	 * the video element (html) is paused, we detect an error.
	 *
	 * If the error goes away in the next cycle we can ignore it, but if it persists after 5 cycles we try to reinitialize the axis player.
	 * */
	private initializePipelineChecker() {
		if (this.pipelineCheckerThread) {
			clearInterval(this.pipelineCheckerThread);
		}
		this.pipelineCheckerThread = setInterval(async () => {
			// Only reconnect when the website is visible
			if (window.document.visibilityState === "visible") {
				// Only reconnect with the required variables are set.
				if (this.axisMediaStreamLibraryVideoOptions.ipAddress && this.axisMediaStreamLibraryVideoOptions.url) {
					// Only reconnect if the axis video player should be ready and playing.
					if (this.avpState === "Ready" || this.avpState === "ReconnectingFailed" || this.avpState === "LoadingFailed") {
						let shouldReconnect = false;

						// Only reconnect if the video element is currently not playing
						let streamEnded = this.axisVideo?.ended;
						if (streamEnded) {
							// ----------------------------------------------------------------------
							// Case 2: If we do not have enough data, we can try to reinitialize
							//         the player. For example in case of a network problem.
							// ----------------------------------------------------------------------
							shouldReconnect = true;
						}

						let streamPaused = this.axisVideo?.paused;
						if (streamPaused) {
							// ----------------------------------------------------------------------
							// Case 3: Sometimes the video element is not in status ended but paused,
							//         although the stream has ended
							// ----------------------------------------------------------------------
							await this.axisVideo?.play().catch(() => {
								shouldReconnect = true;
							});
						}

						if (shouldReconnect) {
							this.avpState = "Reconnecting";
							console.log(`${this.axisMediaStreamLibraryVideoOptions.ipAddress} will be reinitialized.`);
							await this.initializePlayerSafely();
						}
					} else if (this.avpState === "Reconnecting") {
						// Skip

						console.log(`Currently reconnecting camera ${this.axisMediaStreamLibraryVideoOptions.ipAddress}`);
					} else if (this.avpState === "Loading") {
						// Skip

						console.log(`Currently loading camera ${this.axisMediaStreamLibraryVideoOptions.ipAddress}`);
					}
				} else {
					console.log(`Skip camera, because no ip or src was set.`);
				}
			} else {
				console.log(`Skip camera, because document was not visible.`);
			}

			if (this.axisVideo && this.lastCurrentTime !== this.axisVideo.currentTime) {
				this.lastCurrentTime = this.axisVideo.currentTime;
				this.lastTimeUpdate = Date.now();
			}
		}, this.reconnectInterval);
	}

	private destroyPipeline() {
		if (this.pipeline) {
			try {
				this.pipeline.close();
			} catch (e) {
				console.error(e);
			}

			try {
				this.pipeline.rtsp?._clearInterval();
			} catch (e) {
				console.error(e);
			}

			this.pipeline.onSourceOpen = null;
			this.pipeline.ready = null;
			this.pipeline = null;
		} else {
			console.warn("pipeline not set");
		}
	}

	private destroyPlayer() {
		this.destroyPipeline();

		try {
			if (this.axisVideo) {
				this.axisVideo.pause();
				this.axisVideo.removeAttribute("src"); // empty source
				this.axisVideo.currentTime = 0;
			} else {
				console.warn("axisVideo not set");
			}
		} catch (e) {
			console.error("Destroy axisVideoElement Error:" + e.toString());
		}
	}

	private initializePlayerSafely(): Promise<any> {
		if (this.initializePlayerPromise) {
			return this.initializePlayerPromise;
		} else {
			return this.initializePlayer();
		}
	}

	private initializePlayer(): Promise<any> {
		if (this.axisMediaStreamLibraryVideoOptions.ipAddress && this.axisMediaStreamLibraryVideoOptions.url) {
			this.destroyPipeline();
			this.closeVideoStreamStatisticsThread();

			// since we want to reinitialize the player we set the src empty if it was already set.
			// SIMSOFT-7313. happens on drag and drop.
			if (this.axisVideo?.src) {
				this.axisVideo.src = "";
			}

			this.avpState = "Loading";

			this.initializePlayerPromise = new Promise<void>((resolve, reject) => {
				let readyTimeout: ReturnType<typeof setTimeout>;
				let retryOperation = operation({
					forever: true,
					factor: 1,
					minTimeout: 2000,
				});
				this.$OnDestroy.subscribe(() => {
					retryOperation.stop();
					clearTimeout(readyTimeout);
					reject();
				});

				// @TODO should be a setting in the camera.configuration
				const needsAuth = !this.axisMediaStreamLibraryVideoOptions.anonymousAccessAllowed;

				let authTask: Promise<{success: boolean; token: string}>;

				if (needsAuth) {
					authTask = new Promise((resolve, _) => {
						retryOperation.attempt(async (_) => {
							// See:
							// https://github.com/AxisCommunications/media-stream-library-js/blob/master/examples/browser/camera/simple-overlay-player.js
							// https://github.com/AxisCommunications/media-stream-library-js/issues/204
							// https://github.com/AxisCommunications/media-stream-library-js/blob/master/lib/components/ws-source/openwebsocket.ts
							let result = await this._simCameraService
								.getCameraToken(this.axisMediaStreamLibraryVideoOptions.cameraId)
								.then((token) => {
									let websocketSessionToken = "";

									if (token) {
										websocketSessionToken = token;

										return {
											success: true,
											token: websocketSessionToken,
										};
									} else {
										return {
											success: false,
											token: null,
										};
									}
								})
								.catch((err) => {
									retryOperation.retry(err);
								});

							if (result) {
								resolve(result);
							}
						});
					});
				} else {
					authTask = Promise.resolve({
						success: true,
						token: null,
					});
				}

				authTask.then((authResult: {success: boolean; token: string}) => {
					// this.videoElement.addEventListener('suspend', (event) => {
					//    console.log('Data loading has been suspended.');
					// });
					if (authResult.success) {
						const ip = this.axisMediaStreamLibraryVideoOptions.ipAddress;

						const websocketScheme = location.protocol !== "https:" ? "ws" : "wss";
						this.rtspUri = `${this.axisMediaStreamLibraryVideoOptions.url}&videocodec=h264`;

						if (authResult.token) {
							this.websocketUri = `${websocketScheme}://${ip}/rtsp-over-websocket?rtspwssession=${authResult.token}`;
						} else {
							this.websocketUri = `${websocketScheme}://${ip}/rtsp-over-websocket`;
						}

						try {
							this._zone.runOutsideAngular(() => {
								// Setup a new pipeline
								let pipeline = new pipelines.Html5VideoPipeline({
									ws: {uri: this.websocketUri},
									rtsp: {uri: this.rtspUri},
									mediaElement: this.axisVideo,
								});
								this.pipeline = pipeline;

								this.pipeline.onSourceOpen = (
									mse: MediaSource,
									tracks: {
										type: string;
										encoding: string;
										mime: string;
										codec: {
											coding: string;
											profile: string;
											level: string;
										};
									}[],
								) => {
									if (SimConfigurationService.settings.axisVideoPlayerEnableCatchingUp) {
										this.startVideoStreamStatisticThread(mse, tracks);
									}
								};

								readyTimeout = setTimeout(() => {
									if (this.avpState === "Loading") {
										this.avpState = "LoadingFailed";

										reject();
									}
								}, 10 * 1000);

								if (pipeline) {
									//pipeline.ready.then(() => {
									// ---------------------------------------
									// Cancel timeout
									// ---------------------------------------
									if (readyTimeout) {
										clearTimeout(readyTimeout);
										readyTimeout = null;
									}

									if (pipeline) {
										try {
											pipeline.rtsp.play();
										} catch {}
									}

									// console.warn(`${this.axisMediaStreamLibraryVideoId} is now ready.`);

									resolve();
									//});
								} else {
									reject();
								}
							});
						} catch (e) {
							if (readyTimeout) {
								clearTimeout(readyTimeout);
							}
							reject();
						}
					} else {
						this.avpState = "Unauthorized";
						reject();
					}
				});
			}).then(() => {
				this.avpState = "Ready";
			});
		} else {
			if (!this.axisMediaStreamLibraryVideoOptions.ipAddress) {
				console.log("camera ip was not set.");
			}

			if (!this.axisMediaStreamLibraryVideoOptions.url) {
				console.log("camera src was not set.");
			}

			this.initializePlayerPromise = Promise.resolve(null);
		}

		return this.initializePlayerPromise
			.then(
				() => {
					console.log(`${this.axisMediaStreamLibraryVideoOptions.ipAddress} was loaded successfully.`);
				},
				(reason) => {
					console.log(`${this.axisMediaStreamLibraryVideoOptions.ipAddress} failed connecting: ${reason}`);
					this.avpState = "ReconnectingFailed";
				},
			)
			.finally(() => {
				this.initializePlayerPromise = null;
			});
	}

	private startVideoStreamStatisticThread(
		mse: MediaSource,
		tracks: {
			type: string;
			encoding: string;
			mime: string;
			codec: {
				coding: string;
				profile: string;
				level: string;
			};
		}[],
	) {
		if (tracks && tracks.length > 0) {
			const track = tracks[0];

			this.videoStreamStatistic = {
				latency: 0,
				playbackRate: 1,
				droppedFrames: "",
				droppedVideoSegments: 1,
				codec: track.codec,
			};
		} else {
			this.videoStreamStatistic = {
				latency: 0,
				playbackRate: 1,
				droppedFrames: "",
				droppedVideoSegments: 1,
				codec: {
					coding: "Unknown",
					level: "Unknown",
					profile: "Unknown",
				},
			};
		}

		if (!this.updateVideoStreamStatisticThread) {
			if (this.updateVideoStreamStatisticThread) {
				clearInterval(this.updateVideoStreamStatisticThread);
			}
			this.updateVideoStreamStatisticThread = setInterval(() => {
				if (this.axisVideo && mse.activeSourceBuffers && mse.activeSourceBuffers.length > 0) {
					const activeSourceBuffer = mse.activeSourceBuffers[0];

					// Prevents following error message:
					// Failed to execute 'end' on 'TimeRanges': The index provided (4294967295) is greater than the maximum bound (0).
					if (activeSourceBuffer.buffered && activeSourceBuffer.buffered.length > 0) {
						// End time of last buffer in seconds (the first buffer can start any time)
						// The maximum buffer length is about 10 seconds
						// (defined by axis: https://github.com/AxisCommunications/media-stream-library-js/blob/master/lib/components/mse/index.ts
						// in method addSourceBuffer)
						const endBufferTime = activeSourceBuffer.buffered.end(activeSourceBuffer.buffered.length - 1);

						// Current video playback position in seconds
						const videoCurrentTime = !!this.axisVideo ? this.axisVideo?.currentTime : 0;

						// Difference between how much is buffered and what the player is currently playing.
						// Latency in milliseconds
						const latency = (endBufferTime - videoCurrentTime) * 1000;

						if (latency > SimConfigurationService.settings.axisVideoPlayerMaxLatencySkipThreshold) {
							this.axisVideo.currentTime = endBufferTime;
						}

						this.videoStreamStatistic.latency = Math.trunc(latency);

						// Speed up playback rate to catch up with the buffer
						if (latency > SimConfigurationService.settings.axisVideoPlayerMaxLatencyThreshold) {
							if (this.axisVideo?.playbackRate < SimConfigurationService.settings.axisVideoPlayerCatchingUpPlaybackRate) {
								this.axisVideo.playbackRate = SimConfigurationService.settings.axisVideoPlayerCatchingUpPlaybackRate;

								this.videoStreamStatistic.playbackRate = this.axisVideo?.playbackRate;
							}
						}

						// Reset playback rate to normal
						if (latency < SimConfigurationService.settings.axisVideoPlayerMinLatencyThreshold) {
							if (this.axisVideo?.playbackRate > 1.0) {
								this.axisVideo.playbackRate = 1.0;

								this.videoStreamStatistic.playbackRate = this.axisVideo?.playbackRate;
							}
						}
					}
				}
			}, SimConfigurationService.settings.axisVideoPlayerLatencyCheckInterval);
		}
	}

	private closeVideoStreamStatisticsThread() {
		if (this.updateVideoStreamStatisticThread) {
			clearInterval(this.updateVideoStreamStatisticThread);
			this.updateVideoStreamStatisticThread = null;
		} else {
			console.warn("updateVideoStreamStatisticThread not set");
		}
	}

	private closePipelineCheckerThread() {
		if (this.pipelineCheckerThread) {
			clearInterval(this.pipelineCheckerThread);
			this.pipelineCheckerThread = null;
		} else {
			console.warn("closePipelineCheckerThread not set");
		}
	}

	private closeStreamCheckerThread() {
		if (this.streamCheckerThread) {
			clearInterval(this.streamCheckerThread);
			this.streamCheckerThread = null;
		} else {
			console.warn("closeStreamCheckerThreat not set");
		}
	}

	private mute() {
		// if (this.avp && this.avp.currentPlayer) {
		//    this.avp.currentPlayer.volume = 0.0;
		// }
	}

	private unmute() {
		// if (this.avp && his.avp.currentPlayer) {
		//    this.avp.currentPlayer.volume = 1.0;
		// }
	}

	toggleDebugStatistics() {
		this.showDebugStatistics = !this.showDebugStatistics;
		this.debugContainerHeight = this.showDebugStatistics ? 203 : 40;

		this.debugContainer.nativeElement.classList.toggle("hide-scrollbar", this.debugContainerHeight <= 40);
	}
}
